<script setup lang="ts">
import { useQuery } from '@urql/vue';

import { activeArticleFaqPairs } from '~/components/Article/articleFaqs';
import { resolveArticleTemplate } from '~/components/Article/articleTemplates';
import CoachSubpageNotFound from '~/components/Coach/CoachSubpageNotFound.vue';
import { useClusterCountryConfig } from '~/composables/core';
import { useArticleFetchDetail } from '~/composables/Learn/Article/useArticleFetchDetail';
import { useArticleQuizCollection } from '~/composables/Learn/Article/useArticleQuizCollection';
import { useIaPlaces } from '~/composables/unified/useIaPlaces';
import { usePageLookupFailure } from '~/composables/unified/usePageLookupFailure';
import { useSignupCta } from '~/composables/unified/useSignupCta';
import { articlePublicPath, UNIFIED_ROUTE_CAREERS } from '~/constants/routes/unified';
import { fetchArticleKeepReading } from '~/graphql/documents/learn/articles';
import { EntrySource, LearningAssetType } from '~/graphql/generated/graphql';
import { articleBylineDays } from '~/utils/article';
import { fromCampaignDateTime } from '~/utils/campaign';
import { buildArticleSchema, buildFaqPageSchema, stripBrandSuffix, truncateMetaDescription } from '~/utils/seo';

const route = useRoute();
const { t } = useI18n();
const places = useIaPlaces();
const slug = route.params.articleSlug as string;
const considerationAreaSlug = (route.params.considerationAreaSlug as string | undefined) ?? null;
const isPreview = route.query.preview !== undefined;

const articleInput = ref({ slug });

const { article, fetching, error } = await useArticleFetchDetail(articleInput);

const { failed } = usePageLookupFailure(error);

// The fetch matches on the slug alone, so this page serves the article under any area, its
// /articles/:slug fallback and any area it used to live under. Only articlePublicPath is canonical,
// so every other URL is bounced to it and structured data / the sitemap never compete with an alias.
// Inside the coach shell the article's canonical place is the shell's copy of that path.
// The redirect is a 302: editors can move the canonical, and a cached permanent redirect would
// keep sending readers to an old target (or loop between two).
const canonicalPath = computed(() => (article.value ? places.article(article.value) : null));
// clusterRedirectPath: the SSR redirect sets the Location header verbatim (router base NOT
// applied), so the cluster prefix must be prepended or /au traffic lands on the US apex.
const redirectToCanonical = (path: string | null) => (path && route.path !== path
  ? navigateTo({ path: clusterRedirectPath(path), query: route.query }, { replace: true, redirectCode: 302 })
  : undefined);
await redirectToCanonical(canonicalPath.value);
// On a client-side navigation the fetch is lazy, so the article resolves after setup.
if (import.meta.client) {
  watch(canonicalPath, path => void redirectToCanonical(path));
}

// Reactive useSeoMeta: nuxt-schema-org infers the WebPage node's name/description/image from
// these head tags (resolved at SSR render, after the async article loads). SEO always reflects
// the published content, never the draft — a `?preview` request never leaks unpublished copy
// into crawlable meta tags (matching the render gate's no-draft-leak guarantee below).
const articleData = computed(() => article.value?.publishedContent?.data ?? null);

// The standfirst fallback is free-form CMS copy, so clamp through the shared 160-char
// truncation like the other public pages.
const metaDescription = computed(() => {
  const description = article.value?.meta?.description || articleData.value?.standfirst;
  return description ? truncateMetaDescription(description) : undefined;
});

// Quiz-landing articles carry no cover of their own — their share image is the
// linked collection's cover, resolved through the same query the quiz card uses.
const quizCollectionId = computed(() => articleData.value?.quizLanding?.quizCollectionId ?? '');
const { collection: quizCollection } = useArticleQuizCollection(quizCollectionId);
const shareImageUrl = computed(() =>
  article.value?.coverImage?.url ?? quizCollection.value?.coverImage?.publicUrl ?? undefined);

// Crawlers get the publish day in the cluster timezone with its offset spelled out, so a
// back-date anchored to cluster-local midnight is never read as the previous UTC day.
const { clusterTimezone } = useClusterCountryConfig();
const publishedAtIso = computed(() => fromCampaignDateTime(article.value?.publishedAt, clusterTimezone.value, 'YYYY-MM-DDTHH:mm:ssZ') ?? undefined);
// Modified time follows the byline: only a later publish that the byline shows as an update.
const lastPublishedAtIso = computed(() => (article.value && articleBylineDays(article.value, clusterTimezone.value).lastUpdatedDay
  ? fromCampaignDateTime(article.value.lastPublishedAt, clusterTimezone.value, 'YYYY-MM-DDTHH:mm:ssZ') ?? undefined
  : undefined));

useSeoMeta({
  // CMS titles may carry the "| Anyway" brand that the global title template re-appends; strip
  // it (matching the other public pages) and fall back to the article name.
  title: () => stripBrandSuffix(article.value?.meta?.title) || article.value?.name,
  description: () => metaDescription.value,
  ogTitle: () => stripBrandSuffix(article.value?.meta?.title) || article.value?.name,
  ogDescription: () => metaDescription.value,
  ogImage: () => shareImageUrl.value,
  ogType: 'article',
  articlePublishedTime: () => publishedAtIso.value,
  articleModifiedTime: () => lastPublishedAtIso.value,
  articleAuthor: () => (articleData.value?.authorName ? [articleData.value.authorName] : undefined),
  // Preview URLs serve published (or draft) content on a query-string variant of the public
  // URL — keep crawlers off the duplicate, matching the repo's noindex-private-variant rule.
  robots: () => (isPreview ? 'noindex, nofollow' : undefined),
});

// Article structured data (SSR): built from published content only, like the meta tags above —
// a preview request never leaks draft copy into JSON-LD. Guarded on the resolved article (a
// missing slug hard-404s server-side before this; SPA navigations don't need head schema).
const cluster = useCurrentCluster();
if (article.value?.publishedContent) {
  const articleUrl = `${cluster.baseUrl.value}${articlePublicPath(article.value)}`;

  // The node is computed so the share image (async for quiz articles, which
  // resolve it from their collection) lands in the SSR JSON-LD.
  useSchemaOrg([computed(() => buildArticleSchema({
    url: articleUrl,
    headline: stripBrandSuffix(article.value?.meta?.title) || article.value?.name || '',
    description: metaDescription.value,
    imageUrl: shareImageUrl.value,
    datePublished: publishedAtIso.value,
    dateModified: lastPublishedAtIso.value,
    authorName: articleData.value?.authorName || undefined,
  }))]);

  // Mirror only the FAQ section the active template renders — inactive namespaces
  // persist across template switches and may hold retired FAQ copy that must not
  // reach structured data.
  const faqPage = buildFaqPageSchema({ url: articleUrl, faqs: activeArticleFaqPairs(articleData.value) });
  if (faqPage) {
    useSchemaOrg([faqPage]);
  }
}

// useBreadcrumbItems auto-generates the trail (cluster-prefixed URLs) and emits the schema.org
// BreadcrumbList. Override the labels with our i18n nav strings so they track the cluster
// locale; the leaf is added only once the async article name resolves. Overrides apply by path
// segment, so /articles/:slug (one segment fewer) has no area crumb to drop.
useBreadcrumbItems({
  // The consideration-area segment is dropped from the trail and the emitted BreadcrumbList.
  // Until the hubs-enabled flag is on, that path shape is served by the industry page, which
  // 404s an area slug. Once hubs ship it is the area's hub, and this crumb can link to it at
  // flag cleanup.
  overrides: () => [
    { label: t('unified.nav.home') },
    { label: t('unified.nav.careerAdvice'), to: UNIFIED_ROUTE_CAREERS },
    ...(considerationAreaSlug ? [false as const] : []),
    ...(article.value?.name ? [{ label: article.value.name }] : []),
  ],
});

// Non-critical for SEO of THIS page, so it's fine to also fire (non-blocking) during SSR — it
// isn't awaited and never blocks first paint or the SSR fetch above. Paused until the article
// resolves.
const { data: keepReadingData } = useQuery({
  query: fetchArticleKeepReading,
  variables: computed(() => ({
    // Headroom over the 3 rendered: the current article is filtered out below.
    first: 6,
    filters: {
      types: [LearningAssetType.Article],
    },
  })),
  pause: computed(() => !article.value),
});

const keepReading = computed(() =>
  (keepReadingData.value?.learningAssets?.data ?? [])
    .filter(asset => asset.slug !== slug)
    .map(asset => ({ id: asset.id, name: asset.name, to: places.article(asset) }))
    .slice(0, 3),
);

// Articles aren't a savable entity type, so unlike the industry/job pages this carries no
// saved-entity query param.
const { showSignUpCta, signUpLink, trackSignUpClick } = useSignupCta({
  entrySource: EntrySource.ArticlePage,
  page: 'career_advice_article',
  trackProps: { articleSlug: slug },
});

// The draft-vs-published choice is made once here; the render guard, template component and
// preview flag below all derive from it.
const showDraft = computed(() => isPreview && !!article.value?.draftContent);
const activeContent = computed(() => (showDraft.value ? article.value?.draftContent : article.value?.publishedContent) ?? null);

const analytics = useAnalyticsService();
onMounted(() => {
  analytics.track(EVENTS.UNIFIED_PUBLIC_PAGE_VIEWED, { page: 'career_advice_article', articleSlug: slug });
});

const handleSignUpClick = () => {
  if (isPreview) {
    return;
  }
  trackSignUpClick();
};
</script>

<template>
  <div>
    <div v-auto-animate class="flex-1">
      <CoachSubpageNotFound v-if="failed" />

      <template v-else-if="fetching">
        <KUICoreContainerCentered class="py-32">
          <KUICoreLoaderDot />
        </KUICoreContainerCentered>
      </template>

      <template v-else-if="article && activeContent">
        <component
          :is="resolveArticleTemplate(activeContent.data?.template).component"
          :article="article"
          :preview="showDraft"
          :keep-reading="keepReading"
          :sign-up-link="signUpLink"
          :show-sign-up-cta="showSignUpCta"
          class="mb-24"
          @sign-up-click="handleSignUpClick"
        />
      </template>

      <template v-else>
        <KUICoreContainerCentered class="py-32">
          <KUICoreStateMessage
            width="full"
            :title="$t('unified.errors.pageNotFound')"
            :description="$t('general.noRecords')"
          />
        </KUICoreContainerCentered>
      </template>
    </div>
  </div>
</template>
